import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

let scannerSeq = 0;

// Camera QR reader shared by the member app (scanning the gym's code) and the Desk app
// (scanning a member's code). Deliberately handles the two failure modes that actually happen
// on real phones rather than assuming the happy path: permission denied, and no camera at all —
// both surface a plain-language reason plus whatever fallback the caller passes in.
export function QrScanner({ onScan, onCancel, fallback = null, height = 260 }) {
  const [error, setError] = useState(null);
  const elementIdRef = useRef(`qr-scanner-${++scannerSeq}`);
  const scannerRef = useRef(null);
  const handledRef = useRef(false);

  useEffect(() => {
    const elementId = elementIdRef.current;
    let cancelled = false;
    const scanner = new Html5Qrcode(elementId, { verbose: false });
    scannerRef.current = scanner;

    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (decoded) => {
          // The camera fires repeatedly on the same code — only act on the first read.
          if (handledRef.current) return;
          handledRef.current = true;
          onScan(decoded);
        },
        () => {}, // per-frame "no QR in view" — normal, not an error worth surfacing
      )
      .catch((e) => {
        if (cancelled) return;
        const name = e?.name || "";
        if (name === "NotAllowedError" || String(e).includes("Permission")) {
          setError("Camera access was blocked. Allow it in your browser settings, or use the PIN instead.");
        } else if (name === "NotFoundError") {
          setError("No camera found on this device — use the PIN instead.");
        } else {
          setError("Couldn't start the camera. Use the PIN instead.");
        }
      });

    return () => {
      cancelled = true;
      // stop() rejects if the camera never actually started; nothing to clean up in that case.
      scanner.stop().then(() => scanner.clear()).catch(() => {});
    };
  }, [onScan]);

  if (error) {
    return (
      <div className="space-y-3">
        <p className="text-xs" style={{ color: "#D6202B" }}>{error}</p>
        {fallback}
        {onCancel && <button onClick={onCancel} className="text-xs font-semibold" style={{ color: "#A1A1AA" }}>Back</button>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div id={elementIdRef.current} style={{ width: "100%", minHeight: height, borderRadius: 12, overflow: "hidden" }} />
      <p className="text-xs text-center" style={{ color: "#A1A1AA" }}>Point your camera at the gym's QR code</p>
      {onCancel && <button onClick={onCancel} className="w-full text-xs font-semibold" style={{ color: "#A1A1AA" }}>Cancel</button>}
    </div>
  );
}
